// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Button,
  SpaceBetween,
  StatusIndicator,
} from '@amzn/awsui-components-react-v3/polaris';
import ApiClient from '../../../ApiClient';
import DetailsPanel from '../../Common/DetailsPanel';
import VersionHeader from '../../Common/VersionHeader';
import { useVersionedRecord, VersionedRecordPage } from '../../Common/VersionedRecordPage';
import { LocalesState } from '../../../utils/locales';
import { LatestChangeSet } from '../ChangeSets/changeSets';
import { convertDateTimePretty, valueOrDash } from '../../../utils/ReadableConverter';
import { LoadClient } from '../ScopingModels/useLoaded';
import DeleteDatasetModal from './DeleteDatasetModal';
import GoldenDatasetHistory from './GoldenDatasetHistory';
import GoldenDatasetRowsTable from './GoldenDatasetRowsTable';
import GoldenDatasetRowsUpload from './GoldenDatasetRowsUpload';
import * as goldenDatasets from './goldenDatasets';

// The rows are shown by locale code, so the page needs no locales.
const NO_LOCALES: LocalesState = { locales: null, errors: [], reload: () => undefined };

/** A model's name, and whether it is live, unless it no longer exists. */
const ModelValue = ({
  dataset,
  side,
  name,
}: {
  dataset: goldenDatasets.GoldenDataset;
  side: goldenDatasets.ModelSide;
  name: string;
}): JSX.Element => {
  const state = goldenDatasets.modelState(dataset, side);
  return (
    <SpaceBetween direction="horizontal" size="xs">
      <span id={`${side}ModelValue`}>{name}</span>
      {state !== 'missing' && (
        <span id={`${side}ModelState`}>
          <StatusIndicator type={state === 'live' ? 'success' : 'pending'}>
            {goldenDatasets.modelStateLabel(state === 'live')}
          </StatusIndicator>
        </span>
      )}
    </SpaceBetween>
  );
};

interface ContentProps {
  apiClient: LoadClient;
  dataset: goldenDatasets.GoldenDataset;
  canWrite: boolean;
  // Rejects with the API error when the replace fails.
  onReplaceRows: (rows: goldenDatasets.UploadedRow[]) => Promise<void>;
  onReload: () => void;
  onDelete: () => void;
}

/**
 * A dataset's details with each model's state, the reasons cost impacts skip it, its rows and its
 * history; an upload replaces the rows.
 */
const GoldenDatasetContent = ({
  apiClient,
  dataset,
  canWrite,
  onReplaceRows,
  onReload,
  onDelete,
}: ContentProps): JSX.Element => {
  const [uploading, setUploading] = useState(false);
  return (
    <SpaceBetween size="m">
      {/* VersionHeader reads createdAt as the shown version's time; a dataset's is version 1's. */}
      <VersionHeader
        title={dataset.name}
        record={{ version: dataset.version, author: dataset.author, createdAt: dataset.updatedAt }}
        actions={
          canWrite ? (
            <SpaceBetween direction="horizontal" size="xs">
              <Button id="deleteGoldenDataset" onClick={onDelete}>
                Delete
              </Button>
              <Button id="uploadGoldenDatasetRows" onClick={(): void => setUploading(!uploading)}>
                {uploading ? 'Cancel upload' : 'Upload rows'}
              </Button>
            </SpaceBetween>
          ) : (
            undefined
          )
        }
      />
      {goldenDatasets.skippingWarnings(dataset).map(warning => (
        <Alert key={warning.code} id={`goldenDatasetWarning-${warning.code}`} type="warning">
          {`${goldenDatasets.SKIPPED_IN_IMPACT}: ${warning.message}`}
        </Alert>
      ))}
      <DetailsPanel
        items={[
          {
            label: 'Description',
            value: <span id="descriptionValue">{valueOrDash(dataset.description)}</span>,
          },
          {
            label: 'Pricing model',
            value: <ModelValue dataset={dataset} side="pricing" name={dataset.pricingModelName} />,
          },
          {
            label: 'Scoping model',
            value: <ModelValue dataset={dataset} side="scoping" name={dataset.scopingModelName} />,
          },
          { label: 'Dataset created', value: convertDateTimePretty(dataset.createdAt) },
        ]}
      />
      {uploading && (
        <GoldenDatasetRowsUpload
          apiClient={apiClient}
          onReplace={(rows): Promise<void> => onReplaceRows(rows).then(() => setUploading(false))}
          onReload={onReload}
        />
      )}
      <GoldenDatasetRowsTable rows={dataset.rows} />
      {/* Keyed on the version so an upload lists the version it wrote. */}
      <GoldenDatasetHistory
        key={dataset.version}
        apiClient={apiClient}
        datasetId={dataset.datasetId}
      />
    </SpaceBetween>
  );
};

interface GoldenDatasetDetailProps {
  apiClient: {
    httpGet: (path: string) => Promise<unknown>;
    httpPut: (path: string, body: object) => Promise<unknown>;
    httpDelete: (path: string, body?: object) => Promise<unknown>;
  };
  datasetId: string;
  canWrite: boolean;
  navigate: (path: string, state: goldenDatasets.ListState) => void;
}

/** The live version of a golden dataset. */
export const GoldenDatasetDetail = ({
  apiClient,
  datasetId,
  canWrite,
  navigate,
}: GoldenDatasetDetailProps): JSX.Element => {
  // A dataset has no change sets, so the page finds none awaiting approval.
  const fetchDataset = useCallback(
    () =>
      apiClient
        .httpGet(goldenDatasets.datasetPath(datasetId))
        .then(goldenDatasets.toGoldenDataset) as Promise<
        goldenDatasets.GoldenDataset & LatestChangeSet
      >,
    [apiClient, datasetId]
  );
  const page = useVersionedRecord(fetchDataset, goldenDatasets.describeError);
  const { record } = page;
  const [deleting, setDeleting] = useState(false);

  const replaceRows = (rows: goldenDatasets.UploadedRow[]): Promise<void> =>
    page.save(
      () =>
        apiClient
          .httpPut(
            goldenDatasets.rowsPath(datasetId),
            goldenDatasets.toReplaceRowsRequest(page.record ? page.record.version : 0, rows)
          )
          .then(goldenDatasets.toGoldenDataset) as Promise<
          goldenDatasets.GoldenDataset & LatestChangeSet
        >
    );

  return (
    <VersionedRecordPage
      idPrefix="goldenDataset"
      page={page}
      locales={NO_LOCALES}
      loadingText="Loading golden dataset"
      loadErrorHeader="Could not load the golden dataset"
      retryId="retryGoldenDataset"
    >
      {record && (
        <GoldenDatasetContent
          apiClient={apiClient}
          dataset={record}
          canWrite={canWrite}
          onReplaceRows={replaceRows}
          onReload={page.reload}
          onDelete={(): void => setDeleting(true)}
        />
      )}
      {record && deleting && (
        <DeleteDatasetModal
          apiClient={apiClient}
          dataset={record}
          onCancel={(): void => setDeleting(false)}
          onDeleted={(): void =>
            navigate(goldenDatasets.GOLDEN_DATASETS_ROUTE, { deletedName: record.name })
          }
        />
      )}
    </VersionedRecordPage>
  );
};

GoldenDatasetDetail.propTypes = {
  apiClient: PropTypes.object.isRequired,
  datasetId: PropTypes.string.isRequired,
  canWrite: PropTypes.bool.isRequired,
  navigate: PropTypes.func.isRequired,
};

const GoldenDatasetDetailView = (
  props: Omit<GoldenDatasetDetailProps, 'apiClient'>
): JSX.Element => <GoldenDatasetDetail apiClient={ApiClient} {...props} />;

export default GoldenDatasetDetailView;
